import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';

import { catchError, ignoreElements, Observable, throwError, UnaryFunction } from 'rxjs';

export function catchElseRethrow<R, E = HttpErrorResponse>(
  predicate: UnaryFunction<E, boolean>,
  handler: UnaryFunction<E, Observable<R>>,
) {
  return <T>(source: Observable<T>): Observable<T | R> =>
    source.pipe(catchError((res: E) => (predicate(res) ? handler(res) : throwError(() => res))));
}

export function catchBadRequest<R>(
  code: ErrorCodes | ErrorCodes[] | string,
  handler: (res: HttpErrorResponse) => Observable<R>,
) {
  return catchElseRethrow((res) => isBadRequest(res, code), handler);
}

export function catchTaskReassignedBadRequest(handler: (res: HttpErrorResponse) => Observable<unknown>) {
  return catchBadRequest<never>('REQUEST_TASK_ACTION1001', (res) => handler(res).pipe(ignoreElements()));
}

export function catchErrorCode<R>(code: string, handler: (res: HttpErrorResponse) => Observable<R>) {
  return catchElseRethrow((res) => res instanceof HttpErrorResponse && res.error?.code === code, handler);
}

export function isBadRequest(res: unknown, codes?: ErrorCodes | ErrorCodes[] | string): res is BadRequest {
  return (
    res instanceof HttpErrorResponse &&
    res.status === 400 &&
    (codes === undefined ||
      (typeof codes === 'string' && codes === res.error.code) ||
      (Array.isArray(codes) && codes.includes(res.error.code)))
  );
}

export class ErrorCodes {
  static ACCOUNT1004 = 'ACCOUNT1004';
  static ACCOUNT1005 = 'ACCOUNT1005';
  static ACCOUNT1006 = 'ACCOUNT1006';
  static ACCOUNT1007 = 'ACCOUNT1007';
  static ACCOUNT1010 = 'ACCOUNT1010';
  static ACCOUNT_CONTACT1001 = 'ACCOUNT_CONTACT1001';
  static ACCOUNT_CONTACT1002 = 'ACCOUNT_CONTACT1002';
  static ACCOUNT_CONTACT1003 = 'ACCOUNT_CONTACT1003';
  static AUTHORITY1001 = 'AUTHORITY1001';
  static AUTHORITY1003 = 'AUTHORITY1003';
  static AUTHORITY1004 = 'AUTHORITY1004';
  static AUTHORITY1005 = 'AUTHORITY1005';
  static AUTHORITY1006 = 'AUTHORITY1006';
  static AUTHORITY1007 = 'AUTHORITY1007';
  static AUTHORITY1009 = 'AUTHORITY1009';
  static AUTHORITY1014 = 'AUTHORITY1014';
  static AUTHORITY1015 = 'AUTHORITY1015';
  static AUTHORITY1016 = 'AUTHORITY1016';
  static CCAAUTHORITY1004 = 'CCAAUTHORITY1004';
  static CCAAUTHORITY1001 = 'CCAAUTHORITY1001';
  static CCAAUTHORITY1002 = 'CCAAUTHORITY1002';
  static CCAAUTHORITY1003 = 'CCAAUTHORITY1003';
  static EMAIL1001 = 'EMAIL1001';
  static EXTCONTACT1000 = 'EXTCONTACT1000';
  static EXTCONTACT1001 = 'EXTCONTACT1001';
  static EXTCONTACT1002 = 'EXTCONTACT1002';
  static EXTCONTACT1003 = 'EXTCONTACT1003';
  static OTP1001 = 'OTP1001';
  static REQUEST_TASK_ACTION1001 = 'REQUEST_TASK_ACTION1001';
  static TOKEN1001 = 'TOKEN1001';
  static USER1001 = 'USER1001';
  static USER1003 = 'USER1003';
  static USER1004 = 'USER1004';
  static USER1005 = 'USER1005';
  static VERBODY1001 = 'VERBODY1001';
  static VERBODY1002 = 'VERBODY1002';
  static NOTIF1000 = 'NOTIF1000';
  static NOTIF1001 = 'NOTIF1001';
  static NOTIF1002 = 'NOTIF1002';
  static NOTIF1003 = 'NOTIF1003';
  static REPORT1001 = 'REPORT1001';
  static MIREPORT1001 = 'MIREPORT1001';
  static FORM1001 = 'FORM1001';
  static BATCHREISSUE0001 = 'BATCHREISSUE0001';
  static BATCHREISSUE0002 = 'BATCHREISSUE0002';
  static AER1008 = 'AER1008';
  static NOTFOUND1001 = 'NOTFOUND1001';
  static ACCOUNT1001 = 'ACCOUNT1001';
  static SECTOR1001 = 'SECTOR1001';
  static SECTOR1002 = 'SECTOR1002';
  static UNA1001 = 'UNA1001';
  static UNA1002 = 'UNA1002';
  static UNAV1001 = 'UNAV1001';
  static UNAV1002 = 'UNAV1002';
  static UNAV1004 = 'UNAV1004';
}

export interface BadRequest extends HttpErrorResponse {
  status: HttpStatusCode;
  error: {
    code: string;
    data: unknown;
  };
}
